import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RoutingClassBadge, SyncGateBadge } from "@/components/AIBadges";
import { BUYER_POLICY_MODES, ROUTING_CLASSES, ADJUDICATION_STATUSES, SCREENING_PENDING_MESSAGE, humanise } from "@/lib/aiProvenance";
import { ShieldCheck, RefreshCw, Download, ScrollText, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function AIProvenance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [dash, setDash] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ routing_class: "", adjudication_status: "" });
  const [policyOpen, setPolicyOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [d, a, p] = await Promise.all([
        api.get("/ai-provenance/dashboard"),
        api.get("/ai-provenance/assessments", { params: filter }),
        api.get("/ai-buyer-policies"),
      ]);
      setDash(d.data); setAssessments(a.data.items || []); setPolicies(p.data.items || []);
    } catch (e) { toast.error(errMsg(e, "Unable to load AI provenance")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [filter.routing_class, filter.adjudication_status]);

  const totals = dash?.totals || {};
  const provider = dash?.provider;

  async function downloadCsv() {
    try {
      const r = await api.get("/ai-provenance/exports/csv");
      const blob = new Blob([r.data?.csv || ""], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `kandake-ai-provenance-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, "Export failed")); }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl" data-testid="ai-provenance-page">
      <PageHeader
        icon={ShieldCheck}
        title="AI Provenance & Eligibility"
        subtitle="Kandake Iron Gate — evidence-based provenance, adjudication, and commercial routing"
      />

      <Card>
        <CardContent className="p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 mt-0.5" style={{ color: provider?.configured ? "hsl(140 55% 32%)" : "hsl(347 72% 22%)" }} />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Detection provider</div>
            <div className="font-medium" data-testid="ai-dashboard-provider-status">
              {provider?.configured ? `Configured — ${provider.provider_key} / ${provider.provider_model}` : SCREENING_PENDING_MESSAGE}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="ai-dashboard-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="ai-dashboard-export-csv"><Download className="w-3.5 h-3.5 mr-1" />Export CSV</Button>
          </div>
        </CardContent>
      </Card>

      {/* Totals grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-dashboard-totals">
        {[
          ["Total tracks","total_tracks"],
          ["Verified no-AI","verified_no_ai_history"],
          ["Human final / AI history","human_final_with_ai_history"],
          ["AI-assisted","ai_assisted"],
          ["AI-generated","ai_generated"],
          ["Unknown","unknown"],
          ["Conflicted","conflicted"],
          ["Sync-blocked","sync_blocked"],
        ].map(([label, key]) => (
          <Card key={key}>
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
              <div className="text-2xl font-medium" data-testid={`ai-total-${key}`}>{totals[key] ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="assessments">
        <TabsList>
          <TabsTrigger value="assessments" data-testid="ai-dashboard-tab-assessments"><ScrollText className="w-3.5 h-3.5 mr-1" />Assessments</TabsTrigger>
          <TabsTrigger value="policies" data-testid="ai-dashboard-tab-policies"><Building2 className="w-3.5 h-3.5 mr-1" />Buyer Policies</TabsTrigger>
        </TabsList>

        <TabsContent value="assessments">
          <div className="flex gap-2 items-end my-3">
            <div className="w-56">
              <Label>Routing class</Label>
              <Select value={filter.routing_class || "ALL"} onValueChange={(v) => setFilter({ ...filter, routing_class: v === "ALL" ? "" : v })}>
                <SelectTrigger data-testid="ai-filter-routing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {ROUTING_CLASSES.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <Label>Adjudication</Label>
              <Select value={filter.adjudication_status || "ALL"} onValueChange={(v) => setFilter({ ...filter, adjudication_status: v === "ALL" ? "" : v })}>
                <SelectTrigger data-testid="ai-filter-adjudication"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {ADJUDICATION_STATUSES.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {(assessments.length === 0) && (
                  <div className="p-4 text-sm text-muted-foreground" data-testid="ai-assessments-empty">No assessments yet.</div>
                )}
                {assessments.map(a => (
                  <div key={a.id} className="p-3 flex items-center gap-3" data-testid={`ai-assessment-row-${a.id}`}>
                    <RoutingClassBadge value={a.overall_routing_class} />
                    <span className="text-[12px] text-muted-foreground">{humanise(a.adjudication_status)}</span>
                    <Link to={`/tracks/${a.track_id}?tab=ai-provenance`} className="ml-2 text-sm underline">{a.track_id}</Link>
                    <span className="text-[11px] text-muted-foreground ml-auto">{a.updated_at || a.created_at}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies">
          <div className="flex justify-end mb-3">
            {isAdmin && (
              <Button size="sm" onClick={() => { setEditingPolicy(null); setPolicyOpen(true); }} data-testid="ai-add-policy" style={{ background: "hsl(347 72% 22%)" }}>New Buyer Policy</Button>
            )}
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {policies.length === 0 && <div className="p-4 text-sm text-muted-foreground" data-testid="ai-policies-empty">No buyer policies recorded.</div>}
                {policies.map(p => (
                  <div key={p.id} className="p-3 flex items-center gap-3" data-testid={`ai-policy-row-${p.id}`}>
                    <span className="font-medium">{p.organisation_name}</span>
                    <span className="text-[12px] text-muted-foreground">{humanise(p.policy_mode)}</span>
                    <span className={`text-[11px] ${p.is_active ? "" : "text-muted-foreground"}`}>{p.is_active ? "Active" : "Archived"}</span>
                    <div className="ml-auto flex gap-2">
                      {isAdmin && <Button size="sm" variant="outline" onClick={() => { setEditingPolicy(p); setPolicyOpen(true); }} data-testid={`ai-policy-edit-${p.id}`}>Edit</Button>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <BuyerPolicyDialog open={policyOpen} onClose={() => setPolicyOpen(false)} initial={editingPolicy} onDone={load} />
    </div>
  );
}

function BuyerPolicyDialog({ open, onClose, initial, onDone }) {
  const [f, setF] = useState({
    organisation_name: "", organisation_type: "",
    policy_mode: "UNKNOWN",
    accepts_ai_generated: false, accepts_ai_assisted: false,
    accepts_human_final_with_ai_history: true,
    accepts_technical_ai: true, accepts_synthetic_voice: false,
    requires_ai_disclosure: true, requires_verified_human_status: false,
    unknown_policy_behavior: "BLOCK", policy_source_reference: "",
    policy_notes: "", is_active: true,
  });
  useEffect(() => { if (initial) setF({ ...f, ...initial }); }, [initial]);
  async function submit() {
    try {
      if (initial?.id) await api.patch(`/ai-buyer-policies/${initial.id}`, f);
      else            await api.post("/ai-buyer-policies", f);
      toast.success("Buyer policy saved.");
      onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-buyer-policy-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit Buyer Policy" : "New Buyer Policy"}</DialogTitle>
          <DialogDescription>Buyer AI policies are never inferred from a buyer's name. Unknown policy means blocked pending confirmation.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>Organisation</Label><Input data-testid="policy-org" value={f.organisation_name} onChange={e => setF({ ...f, organisation_name: e.target.value })} /></div>
          <div><Label>Organisation type</Label><Input value={f.organisation_type} onChange={e => setF({ ...f, organisation_type: e.target.value })} /></div>
          <div><Label>Policy mode</Label>
            <Select value={f.policy_mode} onValueChange={(v) => setF({ ...f, policy_mode: v })}>
              <SelectTrigger data-testid="policy-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{BUYER_POLICY_MODES.map(v => <SelectItem key={v} value={v}>{humanise(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {["accepts_ai_generated","accepts_ai_assisted","accepts_human_final_with_ai_history","accepts_technical_ai","accepts_synthetic_voice","requires_ai_disclosure","requires_verified_human_status","is_active"].map(k => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f[k]} onChange={e => setF({ ...f, [k]: e.target.checked })} data-testid={`policy-${k}`} />
              {humanise(k)}
            </label>
          ))}
          <div className="md:col-span-2"><Label>Policy source reference</Label><Input value={f.policy_source_reference} onChange={e => setF({ ...f, policy_source_reference: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={f.policy_notes} onChange={e => setF({ ...f, policy_notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!f.organisation_name.trim()} data-testid="policy-save" style={{ background: "hsl(347 72% 22%)" }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
